import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Prevent clicks during animation
    const dc = this.details.querySelector('.details-content');
    if (dc && dc.getAnimations().length > 0) return;

    // Smooth close when clicking an open accordion
    if (this.details.open) {
      event.preventDefault();
      this.#animateClose(this);
      return;
    }

    // Smooth open
    event.preventDefault();
    this.#animateOpen();

    // Exclusive group: close sibling accordions when opening
    const group = this.dataset.exclusiveGroup;
    const accordionContainer = this.closest('[data-accordion-exclusive]');

    if (group) {
      // Header-drawer style: explicit group attribute on each accordion-custom
      const parentList = this.parentElement?.parentElement;
      if (parentList) {
        parentList
          .querySelectorAll(`:scope > li > accordion-custom[data-exclusive-group="${group}"]`)
          .forEach((sibling) => {
            if (sibling !== this && sibling.details.open) {
              this.#animateClose(sibling);
            }
          });
      }
    } else if (accordionContainer?.dataset.accordionExclusive === 'true') {
      // Generic accordion block: parent has data-accordion-exclusive="true"
      // Use closest() filter instead of :scope > child — Shopify may wrap blocks in extra elements
      accordionContainer.querySelectorAll('accordion-custom').forEach((sibling) => {
        if (sibling !== this && sibling.closest('[data-accordion-exclusive]') === accordionContainer && sibling.details.open) {
          this.#animateClose(sibling);
        }
      });
    }
  };

  /**
   * Animate opening the accordion (smooth height expand).
   */
  #animateOpen() {
    this.details.open = true;
    const dc = this.details.querySelector('.details-content');
    if (!dc) return;

    const cs = getComputedStyle(dc);
    const targetHeight = dc.scrollHeight;
    const duration = parseInt(
      cs.getPropertyValue('--accordion-anim-speed')
      || cs.getPropertyValue('--drawer-anim-speed')
      || '300', 10
    );
    const easing = cs.getPropertyValue('--accordion-animation-easing').trim()
      || 'cubic-bezier(0.25, 0.1, 0.25, 1.0)';

    const animation = dc.animate(
      [
        { blockSize: '0px', opacity: 0, marginTop: '0px', marginBottom: '0px', paddingTop: '0px', paddingBottom: '0px' },
        {
          blockSize: targetHeight + 'px',
          opacity: 1,
          marginTop: cs.marginTop,
          marginBottom: cs.marginBottom,
          paddingTop: cs.paddingTop,
          paddingBottom: cs.paddingBottom,
        },
      ],
      { duration, easing }
    );

    animation.onfinish = () => {
      // Prevent desktop scroll jump in sticky containers
      if (window.innerWidth > 749) {
        this.summary.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };
  }

  /**
   * Animate closing an accordion (smooth height collapse).
   * @param {AccordionCustom} sibling
   */
  #animateClose(sibling) {
    const dc = sibling.details.querySelector('.details-content');
    if (!dc) {
      sibling.details.open = false;
      return;
    }

    const startHeight = dc.offsetHeight;
    if (startHeight === 0) {
      sibling.details.open = false;
      return;
    }

    const cs = getComputedStyle(dc);
    const duration = parseInt(
      cs.getPropertyValue('--accordion-anim-speed')
      || cs.getPropertyValue('--drawer-anim-speed')
      || '300', 10
    );
    const easing = cs.getPropertyValue('--accordion-animation-easing').trim()
      || 'cubic-bezier(0.25, 0.1, 0.25, 1.0)';

    const animation = dc.animate(
      [
        {
          blockSize: startHeight + 'px',
          opacity: 1,
          marginTop: cs.marginTop,
          marginBottom: cs.marginBottom,
          paddingTop: cs.paddingTop,
          paddingBottom: cs.paddingBottom,
        },
        { blockSize: '0px', opacity: 0, marginTop: '0px', marginBottom: '0px', paddingTop: '0px', paddingBottom: '0px' },
      ],
      { duration, easing, fill: 'forwards' }
    );

    animation.onfinish = () => {
      sibling.details.open = false;
      animation.cancel();

      // Reset nested accordions inside the closed section
      sibling.details.querySelectorAll('accordion-custom details[open]').forEach((d) => {
        d.removeAttribute('open');
      });
    };
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
